import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTheme } from "@mui/styles";
import Loader from "../../../components/Loader/Loader";
import APP_CONSTANTS, {
  brainApproverTabConstants,
} from "../../../utils/config";
import { fetchBrainApprovalData } from "../../../services/api";
import { brainStyles } from "../../../theme/style";
import { Tab, Tabs } from "@mui/material";
import { formatDateToString } from "../../../utils/utils";
import StatusChip from "../components/StatusChip";
import useApi from "../../../hooks/useApi";
import Error from "../../../components/Error/Error";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

export default function ApproverPage() {
  const [batches, setBatches] = useState([]);
  const navigate = useNavigate();
  const user_email = useSelector((state) => state.auth.metadata.email);
  const [tab, setTab] = useState(brainApproverTabConstants.my_task);
  const {
    status: brainApprovalStatus,
    errorMessage: brainApprovalError,
    callApi: fetchBrainLandingPageData,
  } = useApi(fetchBrainApprovalData);
  const theme = useTheme();

  const classes = brainStyles(theme);

  const loadData = async () => {
    const response = await fetchBrainLandingPageData();
    if (response.success) {
      setBatches(response.data || []);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filter logic for tabs
  const myTasks = batches.filter(
    (batch) =>
      batch.assigned_approver?.toLowerCase() === user_email?.toLowerCase() &&
      ["pending", "re-submitted"].includes(batch.status)
  );

  const unassignedTasks = batches.filter(
    (batch) =>
      (!batch.assigned_approver || batch.assigned_approver === "") &&
      ["pending", "re-submitted"].includes(batch.status)
  );

  const allTasks = batches;

  // Determine which batches to display based on the active tab
  let displayedBatches = [];
  if (tab === brainApproverTabConstants.my_task) {
    displayedBatches = myTasks;
  } else if (tab === brainApproverTabConstants.unassigned) {
    displayedBatches = unassignedTasks;
  } else if (tab === brainApproverTabConstants.all_submissions) {
    displayedBatches = allTasks;
  }

  const isDataLoading =
    brainApprovalStatus === APP_CONSTANTS.LOADING ||
    brainApprovalStatus === APP_CONSTANTS.INITIAL;

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 3,
      }}
    >
      {/* Header Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="h1"
            sx={{ fontSize: "1.25rem", color: theme.palette.secondary.main2 }}
          >
            Pending Approvals
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.primary.text2 }}
          >
            Review and approve/reject submitted batches
          </Typography>
        </Box>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          minHeight: 36,
          borderBottom: "0.5px solid #ccc",
          "& .MuiTab-root": {
            minHeight: 36,
            textTransform: "none",
            fontSize: "0.78rem",
            fontWeight: 600,
            px: 1.75,
            color: theme.palette.brain.table.row.text,
          },
          "& .MuiTabs-indicator": {
            backgroundColor: theme.palette.secondary.main2,
            height: 2.5,
          },
        }}
      >
        <Tab
          label={`My Tasks (${isDataLoading ? "..." : myTasks.length})`}
          value={brainApproverTabConstants.my_task}
        />
        <Tab
          label={`Unassigned (${
            isDataLoading ? "..." : unassignedTasks.length
          })`}
          value={brainApproverTabConstants.unassigned}
        />
        <Tab
          label={`All Submissions (${isDataLoading ? "..." : allTasks.length})`}
          value={brainApproverTabConstants.all_submissions}
        />
      </Tabs>

      {/* Table */}
      {isDataLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            width: "100%",
            height: "100%",
          }}
        >
          <Loader
            message={"Loading data..."}
            showLoadingImage={false}
            top={"50%"}
            circleStyle={{ color: theme.palette.secondary.main2 }}
          />
        </Box>
      ) : brainApprovalStatus === APP_CONSTANTS.SUCCESS ? (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            border: `1px solid ${theme.palette.brain.mis.grey.border1}`,
            backgroundColor: theme.palette.brain.table.tableBackground,
            borderRadius: 2,
            "&::-webkit-scrollbar": { height: 6, width: 6 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.brain.mis.grey.background1,
              borderRadius: 3,
            },
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeader}>
                  Submission ID
                </TableCell>
                <TableCell className={classes.tableHeader}>Rows</TableCell>
                <TableCell className={classes.tableHeader}>Changes</TableCell>

                <TableCell className={classes.tableHeader}>
                  Submitted At
                </TableCell>
                <TableCell className={classes.tableHeader}>
                  Submitted By
                </TableCell>
                <TableCell className={classes.tableHeader}>
                  Assigned To
                </TableCell>
                <TableCell className={classes.tableHeader} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedBatches?.map((batch, idx) => {
                return (
                  <TableRow
                    key={batch.id}
                    hover
                    sx={{
                      cursor: "pointer",
                      backgroundColor:
                        idx % 2 === 0
                          ? "transparent"
                          : theme.palette.brain.table.row.background,
                      "&:hover": {
                        backgroundColor:
                          theme.palette.brain.table.row.hoverBackground,
                      },
                      transition: "background-color 0.12s ease",
                    }}
                  >
                    <TableCell
                      sx={{
                        color: theme.palette.brain.table.row.text,
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                          #{batch.id}
                        </Typography>
                        <StatusChip status={batch.status} />
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.brain.table.row.text,
                      }}
                    >
                      {batch.total_rows}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.brain.table.row.text,
                      }}
                    >
                      {batch.total_changes}
                    </TableCell>

                    <TableCell
                      sx={{
                        color: theme.palette.brain.table.row.text,
                      }}
                    >
                      {formatDateToString(batch.submittedAt, "DD-MMM-YYYY")}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.brain.table.row.text,
                      }}
                    >
                      {batch.submitted_by}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.brain.table.row.text,
                      }}
                    >
                      {batch.assigned_approver === "" ||
                      !batch.assigned_approver
                        ? "Unassigned"
                        : batch.assigned_approver}
                    </TableCell>

                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<OpenInNewIcon />}
                        onClick={() => navigate(`/brain/approver/${batch.id}`)}
                        sx={{
                          textTransform: "none",
                          fontSize: "0.75rem",
                          borderColor: theme.palette.secondary.main2,
                          color: theme.palette.secondary.main2,

                          "& .MuiButton-startIcon svg": {
                            fontSize: 16,
                          },

                          "&:hover": {
                            borderColor: theme.palette.secondary.main2,
                            color: theme.palette.secondary.main2,
                          },
                        }}
                      >
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {displayedBatches?.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    align="center"
                    sx={{ py: 4, color: theme.palette.brain.table.row.text }}
                  >
                    No pending submissions
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Error message={brainApprovalError} />
        </Box>
      )}
    </Box>
  );
}
